import React, { useState } from 'react';
import { Globe, Mail, Loader2, CheckCircle2, Clock, LogIn } from 'lucide-react';
import { Language } from '../types';
import { joinWaitlist } from '../services/geminiService';

interface WaitlistScreenProps {
    language?: Language;
    onToggleLanguage?: () => void;
    onShowLogin: () => void;
    // Set when we arrive here right after a sign-in attempt got rejected by maintenance mode —
    // we already know their email, so skip the form and confirm they've been added.
    autoJoinedEmail?: string | null;
}

export const WaitlistScreen: React.FC<WaitlistScreenProps> = ({
    language = 'english', onToggleLanguage, onShowLogin, autoJoinedEmail,
}) => {
    const vn = language === 'vietnamese';
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>(autoJoinedEmail ? 'done' : 'idle');
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) return;
        setStatus('submitting');
        setError(null);
        try {
            await joinWaitlist(email.trim(), 'landing');
            setStatus('done');
        } catch (err: any) {
            setError(err?.message || (vn ? 'Có lỗi xảy ra, thử lại nhé.' : 'Something went wrong — please try again.'));
            setStatus('error');
        }
    };

    return (
        <div className="min-h-screen w-full bg-gray-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4 pattern-grid relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500/10 rounded-full blur-[100px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-500/10 rounded-full blur-[100px]" />
            </div>

            <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden relative z-10 animate-in fade-in zoom-in-95 duration-500">
                <div className="p-8">
                    <div className="text-center mb-6 relative">
                        {onToggleLanguage && (
                            <button
                                onClick={onToggleLanguage}
                                className="absolute top-0 right-0 min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors flex items-center justify-center gap-1"
                                title={vn ? 'Switch to English' : 'Chuyển sang Tiếng Việt'}
                            >
                                <Globe size={18} />
                                <span className="text-[10px] font-bold uppercase">{vn ? 'VN' : 'EN'}</span>
                            </button>
                        )}
                        <div className="w-20 h-20 mx-auto mb-6">
                            <img src="/logo-mark.png" alt="StudyBoard" className="w-full h-full object-contain" />
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 text-xs font-semibold mb-4">
                            <Clock size={12} /> {vn ? 'Sắp ra mắt' : 'Coming soon'}
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            {vn ? 'StudyBoard đang được nâng cấp' : "We're upgrading StudyBoard"}
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                            {vn
                                ? 'Tụi mình đang tạm dừng đăng ký mới để xây dựng một phiên bản tốt hơn. Để lại email, tụi mình sẽ báo ngay khi mở cửa trở lại.'
                                : "We've paused new sign-ups while we build a better version. Leave your email and we'll let you know the moment we reopen."}
                        </p>
                    </div>

                    {status === 'done' ? (
                        <div className="text-center py-4 animate-in fade-in zoom-in-95 duration-300">
                            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                                <CheckCircle2 size={28} className="text-emerald-500" />
                            </div>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">
                                {vn ? 'Đã thêm bạn vào danh sách chờ!' : "You're on the list!"}
                            </p>
                            <p className="text-xs text-gray-400">
                                {autoJoinedEmail
                                    ? (vn ? `Tụi mình sẽ email cho ${autoJoinedEmail} khi mở lại.` : `We'll email ${autoJoinedEmail} when we reopen.`)
                                    : (vn ? 'Tụi mình sẽ email cho bạn khi mở lại.' : "We'll email you when we reopen.")}
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-3">
                            <div className="relative">
                                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder={vn ? 'Email của bạn' : 'Your email'}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors"
                                />
                            </div>
                            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="w-full py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {status === 'submitting' ? <Loader2 size={16} className="animate-spin" /> : null}
                                {vn ? 'Tham gia danh sách chờ' : 'Join the waitlist'}
                            </button>
                        </form>
                    )}

                    <div className="mt-6 pt-5 border-t border-gray-100 dark:border-slate-700 text-center">
                        <button
                            onClick={onShowLogin}
                            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 font-medium inline-flex items-center gap-1 transition-colors"
                        >
                            <LogIn size={12} /> {vn ? 'Đã có tài khoản? Đăng nhập' : 'Already have an account? Log in'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
